import { useState, type ReactElement } from 'react'
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { FamilyCard, Pressable } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { NlsLocaleSelect } from '../../nls/NlsLocaleSelect'
import { FamilyLeavePanel } from './FamilyLeavePanel'
import { FamilyFeesPanel } from './FamilyFeesPanel'
import { FamilyBookingsPanel } from './FamilyBookingsPanel'
import { FamilyConsentPanel } from './FamilyConsentPanel'
import './familyLeave.css'

export interface FamilyMoreTabProps {
  workspaceId: string
  learnerId: string
  frozen?: boolean
  onOpenMeetings?: () => void
}

export const FamilyMoreTab = ({
  workspaceId,
  learnerId,
  frozen = false,
  onOpenMeetings,
}: FamilyMoreTabProps): ReactElement => {
  const { t } = useNls()
  const [activePanel, setActivePanel] = useState<'leave' | 'fees' | 'bookings' | 'consent' | null>(null)

  if (frozen) {
    return (
      <div className="familyMoreTab">
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyMoreTab__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
      </div>
    )
  }

  if (activePanel === 'leave') {
    return (
      <FamilyLeavePanel
        workspaceId={workspaceId}
        learnerId={learnerId}
        frozen={frozen}
        onBack={() => setActivePanel(null)}
      />
    )
  }

  if (activePanel === 'fees') {
    return (
      <FamilyFeesPanel
        workspaceId={workspaceId}
        learnerId={learnerId}
        frozen={frozen}
        onBack={() => setActivePanel(null)}
      />
    )
  }

  if (activePanel === 'bookings') {
    return (
      <FamilyBookingsPanel
        workspaceId={workspaceId}
        learnerId={learnerId}
        frozen={frozen}
        onBack={() => setActivePanel(null)}
      />
    )
  }

  if (activePanel === 'consent') {
    return (
      <FamilyConsentPanel
        workspaceId={workspaceId}
        learnerId={learnerId}
        onBack={() => setActivePanel(null)}
      />
    )
  }

  return (
    <div className="familyMoreTab">
      <FamilyCard title={t('NLSFamilyMoreCardHeading')}>
        <div className="familyMoreTab__list">
          <Pressable
            className="familyMoreTab__row"
            onClick={() => setActivePanel('leave')}
          >
            <div className="familyMoreTab__rowLeft">
              <span className="familyMoreTab__rowIcon" aria-hidden="true">
                <EventNoteRoundedIcon sx={{ fontSize: 20 }} />
              </span>
              <span className="familyMoreTab__rowLabel">
                {t('NLSFamilyMoreAskForLeave')}
              </span>
            </div>
            <span className="familyMoreTab__rowArrow" aria-hidden="true">
              <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
            </span>
          </Pressable>

          <Pressable
            className="familyMoreTab__row"
            onClick={() => setActivePanel('fees')}
          >
            <div className="familyMoreTab__rowLeft">
              <span className="familyMoreTab__rowIcon" aria-hidden="true">
                <ReceiptLongRoundedIcon sx={{ fontSize: 20 }} />
              </span>
              <span className="familyMoreTab__rowLabel">
                {t('NLSFamilyMoreFees')}
              </span>
            </div>
            <span className="familyMoreTab__rowArrow" aria-hidden="true">
              <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
            </span>
          </Pressable>

          <Pressable
            className="familyMoreTab__row"
            onClick={() => setActivePanel('bookings')}
          >
            <div className="familyMoreTab__rowLeft">
              <span className="familyMoreTab__rowIcon" aria-hidden="true">
                <ConfirmationNumberRoundedIcon sx={{ fontSize: 20 }} />
              </span>
              <span className="familyMoreTab__rowLabel">
                {t('NLSFamilyMoreBookings')}
              </span>
            </div>
            <span className="familyMoreTab__rowArrow" aria-hidden="true">
              <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
            </span>
          </Pressable>

          <Pressable
            className="familyMoreTab__row"
            onClick={() => onOpenMeetings?.()}
            data-testid="family-more-ptm-row"
          >
            <div className="familyMoreTab__rowLeft">
              <span className="familyMoreTab__rowIcon" aria-hidden="true">
                <GroupsRoundedIcon sx={{ fontSize: 20 }} />
              </span>
              <span className="familyMoreTab__rowLabel">
                {t('NLSFamilyMorePtm')}
              </span>
            </div>
            <span className="familyMoreTab__rowArrow" aria-hidden="true">
              <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
            </span>
          </Pressable>

          <Pressable
            className="familyMoreTab__row"
            onClick={() => setActivePanel('consent')}
            data-testid="family-more-consent-row"
          >
            <div className="familyMoreTab__rowLeft">
              <span className="familyMoreTab__rowIcon" aria-hidden="true">
                <SecurityRoundedIcon sx={{ fontSize: 20 }} />
              </span>
              <span className="familyMoreTab__rowLabel">
                {t('NLSFamilyMoreConsent')}
              </span>
            </div>
            <span className="familyMoreTab__rowArrow" aria-hidden="true">
              <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
            </span>
          </Pressable>

          <div
            className="familyMoreTab__row familyMoreTab__languageRow"
            data-testid="family-more-language-row"
          >
            <div className="familyMoreTab__rowLeft">
              <span className="familyMoreTab__rowIcon" aria-hidden="true">
                <LanguageRoundedIcon sx={{ fontSize: 20 }} />
              </span>
              <span className="familyMoreTab__rowLabel">
                {t('NLSFamilyMoreLanguage')}
              </span>
            </div>
            <div className="familyMoreTab__rowRight" onClick={(e) => e.stopPropagation()}>
              <NlsLocaleSelect className="familyMoreTab__languageSelect" />
            </div>
          </div>
        </div>
      </FamilyCard>
    </div>
  )
}
